import { holidayYear } from "./holidayBankUtils";

const OPEN_RH_FLAG = "pacehrm_open_restricted_holiday";

/** Session flag: ESS sidebar / deep-link opens RH apply form on Attendance. */
export function requestOpenRestrictedHolidayForm() {
  try {
    sessionStorage.setItem(OPEN_RH_FLAG, "1");
  } catch {
    /* ignore */
  }
}

export function consumeOpenRestrictedHolidayForm() {
  try {
    if (sessionStorage.getItem(OPEN_RH_FLAG) === "1") {
      sessionStorage.removeItem(OPEN_RH_FLAG);
      return true;
    }
  } catch {
    /* ignore */
  }
  return false;
}

/**
 * RH dates available to an employee from assigned holiday policies.
 */
export function getEmployeeRestrictedHolidays({
  employeeId,
  holidayPolicies = [],
  employeeHolidayMappings = {},
  year = null,
}) {
  if (employeeId == null) return [];
  const empId = String(employeeId);
  const policyIds = new Set(
    []
      .concat(
        employeeHolidayMappings[empId] || [],
        employeeHolidayMappings[employeeId] || [],
        employeeHolidayMappings[Number(employeeId)] || []
      )
      .map((id) => String(id))
  );

  let policies = (holidayPolicies || []).filter(
    (p) => p && p.isActive !== false && policyIds.has(String(p.id))
  );
  if (!policies.length) {
    policies = (holidayPolicies || []).filter(
      (p) =>
        p &&
        p.isActive !== false &&
        (p.assignedTo?.allEmployees ||
          (Array.isArray(p.holidays) &&
            p.holidays.some(
              (h) => String(h.type || "").toLowerCase() === "restricted"
            )))
    );
  }

  const yearFilter = year != null && year !== "" ? Number(year) : null;
  const out = [];
  const seen = new Set();
  for (const policy of policies) {
    for (const h of policy.holidays || []) {
      if (String(h.type || "").toLowerCase() !== "restricted") continue;
      const y = holidayYear(h.date);
      if (yearFilter != null && Number.isFinite(yearFilter) && y !== yearFilter) {
        continue;
      }
      const key = `${h.date}|${h.name}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({
        ...h,
        policyId: policy.id,
        policyName: policy.name,
        year: y,
        maxRestrictedHolidaysPerYear: policy.maxRestrictedHolidaysPerYear,
      });
    }
  }
  out.sort((a, b) => String(a.date || "").localeCompare(String(b.date || "")));
  return out;
}

export function employeeHasRestrictedHolidayOption(opts) {
  return getEmployeeRestrictedHolidays(opts).length > 0;
}

/**
 * Usage summary for a calendar year.
 * @returns {{ year: number, total: number|null, utilised: number, balance: number|null, label: string }}
 */
export function buildRestrictedHolidayUsage({
  year,
  maxAllowed,
  utilisedCount = 0,
}) {
  const y = Number(year) || new Date().getFullYear();
  const utilised = Math.max(0, Number(utilisedCount) || 0);
  const total =
    maxAllowed === "" || maxAllowed == null || maxAllowed === undefined
      ? null
      : Math.max(0, Number(maxAllowed) || 0);
  const balance = total == null ? null : Math.max(0, total - utilised);
  const label =
    total == null
      ? `Year ${y}: utilised ${utilised} (no yearly cap set)`
      : `Year ${y}: total allowed ${total}, utilised ${utilised}, balance ${balance}`;
  return { year: y, total, utilised, balance, label };
}

/** Option value for RH select (stable across name/date). */
export function rhOptionValue(h) {
  if (!h) return "";
  if (h.id != null && h.id !== "") return `id:${h.id}`;
  return `date:${String(h.date || "").slice(0, 10)}|${h.name || ""}`;
}

export function formatRhOptionLabel(h, formatDateFn) {
  const datePart =
    typeof formatDateFn === "function"
      ? formatDateFn(h.date)
      : String(h.date || "").slice(0, 10);
  return `${h.name || "Restricted Holiday"} — ${datePart}`;
}

/**
 * Map employee↔policy rows into { [employeeId]: policyId[] }.
 */
export function buildEmployeeHolidayMappings(rows = []) {
  const map = {};
  for (const row of rows || []) {
    const empId = row.employeeId ?? row.employee_id;
    const policyId = row.holidayPolicyId ?? row.holiday_policy_id ?? row.policyId;
    if (empId == null || policyId == null) continue;
    const key = String(empId);
    if (!map[key]) map[key] = [];
    if (!map[key].includes(policyId)) map[key].push(policyId);
  }
  return map;
}
